import { ReactElement, useState, useMemo, useCallback } from "react";
import { Box, Typography, Chip, Link, TableSortLabel, SxProps, Theme } from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { ColumnDef, ValueColumnDefParams } from "@arelia-components/ui-atoms/data-grid/models";
import { PlatformPost, SocialPlatform } from "../../api/postForMe";
import { SortConfig, SortKey, NormalizedMetrics } from "./models";

export interface PlatformConfig {
  icon: ReactElement;
  chipSx: SxProps<Theme>;
  menuIcon: ReactElement;
}

const CAPTION_COLUMN_WIDTH = "360px";

const platformConfigs: Record<string, PlatformConfig> = {
  [SocialPlatform.Facebook]: {
    icon: <FacebookIcon />,
    chipSx: { bgcolor: '#1877F2', color: 'white', '& .MuiChip-icon': { color: 'white' } },
    menuIcon: <FacebookIcon color="primary" fontSize="small" sx={{ mr: 1 }} />,
  },
  [SocialPlatform.Instagram]: {
    icon: <InstagramIcon />,
    chipSx: {
      background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
      color: 'white',
      '& .MuiChip-icon': { color: 'white' },
    },
    menuIcon: <InstagramIcon color="error" fontSize="small" sx={{ mr: 1 }} />,
  },
  [SocialPlatform.TikTok]: {
    icon: <MusicNoteIcon />,
    chipSx: { bgcolor: '#000000', color: 'white', '& .MuiChip-icon': { color: 'white' } },
    menuIcon: <MusicNoteIcon color="secondary" fontSize="small" sx={{ mr: 1 }} />,
  },
};

export function getPlatformConfig(platform: string): PlatformConfig | undefined {
  return platformConfigs[platform.toLowerCase()];
}

const EMPTY_METRICS: NormalizedMetrics = { views: 0, likes: 0, comments: 0, shares: 0 };

export function normalizeMetrics(metrics: any): NormalizedMetrics {
  if (!metrics) return EMPTY_METRICS;

  return {
    likes: metrics.likes ?? metrics.like_count ?? 0,
    comments: metrics.comments ?? metrics.comment_count ?? 0,
    shares: metrics.shares ?? metrics.share_count ?? 0,
    views: metrics.views ?? metrics.view_count ?? metrics.video_views ?? 0,
  };
}

const DEFAULT_SORT: SortConfig = { key: 'date', direction: 'desc' };

function getPostSortValue(post: PlatformPost, key: SortKey): number {
  if (key === 'date') {
    return post.posted_at ? new Date(post.posted_at).getTime() : 0;
  }
  return normalizeMetrics(post.metrics)[key as keyof NormalizedMetrics] || 0;
}

export function useSortedPosts(posts: PlatformPost[]) {
  const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT);

  const handleSort = useCallback((key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const sortedPosts = useMemo(() => {
    if (!posts?.length) return [];

    return [...posts].sort((a, b) => {
      const aValue = getPostSortValue(a, sortConfig.key);
      const bValue = getPostSortValue(b, sortConfig.key);
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [posts, sortConfig]);

  return { sortedPosts, sortConfig, handleSort };
}

function extractThumbnailUrl(media: any): string | null {
  if (!media) return null;

  if (Array.isArray(media) && media.length) {
    const first = media[0];
    return typeof first === 'string' ? first : (first.thumbnail_url || first.url);
  }
  if (typeof media === 'object') {
    return media.thumbnail_url || media.url;
  }
  if (typeof media === 'string') {
    return media;
  }
  return null;
}

function createSortableHeader(
  label: string,
  sortKey: SortKey,
  sortConfig: SortConfig,
  onSort: (key: SortKey) => void
) {
  return (
    <TableSortLabel
      active={sortConfig.key === sortKey}
      direction={sortConfig.key === sortKey ? sortConfig.direction : 'asc'}
      onClick={() => onSort(sortKey)}
    >
      {label}
    </TableSortLabel>
  );
}

function createMetricColumn( label: string, metricKey: keyof NormalizedMetrics, sortConfig: SortConfig, onSort: (key: SortKey) => void ): ColumnDef {
  return {
    field: "metrics",
    headerName: createSortableHeader(label, metricKey, sortConfig, onSort),
    align: "right",
    renderer: ({ value }: ValueColumnDefParams) => (
      <>{normalizeMetrics(value)[metricKey]}</>
    ),
  };
}

export function buildColumns(
  sortConfig: SortConfig, 
  onSort: (key: SortKey) => void
): ColumnDef[] {
  return [
    {
      field: "platform_url",
      headerName: "Preview",
      align: "center",
      width: "100px",
      renderer: ({ value, row }: ValueColumnDefParams) => {
        const thumbnailUrl = extractThumbnailUrl(row.media);

        const content = thumbnailUrl ? (
          <Box component="img" src={thumbnailUrl} alt="Post thumbnail" sx={styles.thumbnailImage} />
        ) : (
          <Box sx={styles.thumbnailPlaceholder}>
            <OpenInNewIcon color="action" />
          </Box>
        );

        return value ? (
          <Link href={value} target="_blank" rel="noopener" sx={styles.centerFlex}>
            {content}
          </Link>
        ) : (
          <Box sx={styles.centerFlex}>{content}</Box>
        );
      },
    },
    {
      field: "caption",
      headerName: "Caption",
      width: CAPTION_COLUMN_WIDTH,
      renderer: ({ row }: ValueColumnDefParams) => (
        <Box sx={styles.captionCell}>
          <Typography noWrap title={row.caption || "(No Caption)"}>
            {row.caption || "(No Caption)"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "platform",
      headerName: "Platform",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => {
        const config = getPlatformConfig(String(value || ''));
        const chipSx = { ...styles.platformChip, ...config?.chipSx } as SxProps<Theme>;
        return (
          <Chip label={value} size="small" icon={config?.icon} sx={chipSx} />
        );
      },
    },
    {
      field: "posted_at",
      headerName: createSortableHeader("Date", "date", sortConfig, onSort),
      align: "right",
      formatter: ({ value }: ValueColumnDefParams) =>
        value ? new Date(value).toLocaleDateString() : "-",
    },
    createMetricColumn("Views", "views", sortConfig, onSort),
    createMetricColumn("Likes", "likes", sortConfig, onSort),
    createMetricColumn("Comments", "comments", sortConfig, onSort),
    createMetricColumn("Shares", "shares", sortConfig, onSort),
  ];
}

const styles = {
  thumbnailImage: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'transform 0.2s',
    '&:hover': { transform: 'scale(1.05)' },
  },
  thumbnailPlaceholder: {
    width: 56,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'action.hover',
    borderRadius: 2,
  },
  centerFlex: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  captionCell: {
    width: CAPTION_COLUMN_WIDTH,
    maxWidth: CAPTION_COLUMN_WIDTH,
    overflow: 'hidden',
  },
  platformChip: {
    fontWeight: 500,
  }
};
